<template>
<section class="flex align-center space-between wrap">
	<Heading :level="2" :text="heading" />
	<div class="info-block flex align-center space-between">
		<p :class="['status', statusInfo.class]">{{ statusInfo.message }}</p>
		<Button v-if="status === 0" class="primary" :text="buttonText" @btnClick="goToForm(applicationType)" />
	</div>
	<template v-if="applicationType !== 'claim'">
		<ul v-if="status === 0 && stepList.length" class="width-100">
			<step-status-item v-for="(item, index) in stepList" :item="item" :key="index" />
		</ul>

		<p v-else-if="status === 1" class="width-100 m-t-1">
			Your application has been submitted. Please check back later for updates.
		</p>
		<p v-else-if="status === 2" class="width-100 m-t-1">
			Your application is being assessed/processed. Please check back later for updates.
		</p>
		<p v-else-if="status === 3" class="width-100 m-t-1">
			Your application is in query. Please provide any additional information requested.
		</p>
		<p v-else-if="status === 4" class="width-100 m-t-1">
			Your application has been rejected.
		</p>
		<p v-else-if="status === 5" class="width-100 m-t-1">
			Your application has been withdrawn. Please get in touch if you'd like to start a new one.
		</p>
	</template>
	<template v-if="getDocumentStatus(`${applicationType}DocumentStatus`, status)">
		<p class="m-t-3">You may be asked to upload further files to support your application/claim during the assessment process. You can do so by visiting the 'Manage Documents' page.</p>
		<router-link
			class="minr-btn secondary text-decoration-none m-t-1"
			:to="manageDocsUrl">
			Manage Documents
		</router-link>
	</template>
</section>
</template>

	<script>
import { mapGetters, mapActions } from 'vuex';

import StepStatusItem from '@/components/StepStatusItem';

export default {
	name: 'ApplicationListItem',
	components: {
		StepStatusItem
	},
	props: {
		heading: {
			type: String,
			default: 'Missing heading'
		},
		steps: {
			type: Array,
			default () {
				return [];
			}
		},
		status: {
			type: Number,
			default: 0
		},
		applicationType: {
			type: String,
			default: 'eoi',
			validator (value) {
				return ['eoi', 'application', 'claim'].includes(value);
			}
		},
		statusText: {
			type: String,
			default: ''
		},
		id: {
			type: String,
			default: ''
		},
		applicationSize: {
			type: String,
			default: 'large',
			validator (value) {
				return ['small', 'large'].includes(value);
			}
		}
	},
	data () {
		return {
			stepList: this.steps,
			eoi: {
				0: { message: 'Not Started', class: 'info' },
				1: { message: 'Submitted', class: 'warning' },
				2: { message: 'Processing', class: 'warning' },
				3: { message: 'In Query', class: 'warning' },
				4: { message: 'Rejected', class: 'error' },
				5: { message: 'Withdrawn', class: 'error' },
				6: { message: 'Approved', class: 'success' },
				inProgress: { message: 'In Progress', class: 'info' }
			},
			application: {
				0: { message: 'Not Started', class: 'info' },
				1: { message: 'Submitted', class: 'warning' },
				2: { message: 'Processing', class: 'warning' },
				3: { message: 'In Query', class: 'warning' },
				4: { message: 'Rejected', class: 'error' },
				5: { message: 'Withdrawn', class: 'error' },
				6: { message: 'Approved', class: 'success' },
				inProgress: { message: 'In Progress', class: 'info' }
			},
			claim: {
				0: { message: 'In Progress', class: 'info' },
				1: { message: 'Submitted', class: 'info' },
				2: { message: 'Processing', class: 'info' },
				3: { message: 'In Query', class: 'info' },
				4: { message: 'Rejected', class: 'error' },
				5: { message: 'Withdrawn', class: 'error' },
				6: { message: 'Paid', class: 'success' }
			},
			applicationTypeMap: {
				eoi: 'ua_icexpressionofinterest',
				application: 'ua_icapplication',
				claim: 'ua_icclaim'
			},
			docsUnlocked: false
		};
	},

	computed: {
		statusInfo () {
			if (this.status === 0 && this.checkInProgress()) {
				return this[this.applicationType].inProgress;
			}
			return this[this.applicationType][this.status] || false;
		},
		buttonText () {
			return this.statusInfo.message === 'Not Started' && this.applicationType !== 'claim' ? 'Get Started' : 'Continue';
		},
		manageDocsUrl () {
			if (this.applicationType === 'claim') {
				return { name: 'DocumentManagement', params: { stage: this.applicationType }, query: { claimId: this.id || null }, props: { applicationSize: this.applicationSize } };
			}
			return { name: 'DocumentManagement', params: { stage: this.applicationType, applicationSize: this.applicationSize }, props: { applicationSize: this.applicationSize } };
		},
			...mapGetters('eoi', ['getEOICurrentStep']),
			...mapGetters('application', ['getApplicationCurrentStep']),
			...mapGetters('settings', ['getDocumentStatus'])
	},
	mounted () {
		this.getDocManagementStatus({ itemType: `${this.applicationType}DocumentStatus`, applicationTypeId: this.applicationTypeMap[this.applicationType], entityId: this.id });
	},
	methods: {
		checkInProgress () {
			if (this.applicationType === 'eoi') {
				return this.getEOICurrentStep !== null;
			}
			if (this.applicationType === 'application') {
				return this.getApplicationCurrentStep(this.applicationSize) !== null;
			}
			return false;
		},
		goToForm (applicationType) {
			// Need to also work out if it's large or small here
			const activeApplication = this.applicationSize;
			if (!activeApplication) {
				return;
			}
			let startLink = '';
			let defaultLink = '';

			switch (applicationType) {
			case 'eoi':
				startLink = this.getEOICurrentStep || 'ContactDetailsEOI';
				break;
			case 'application':
				defaultLink = activeApplication === 'small' ? 'BusinessDetailsSmall' : 'BusinessDetails';
				startLink = this.getApplicationCurrentStep(activeApplication) || defaultLink;
				break;
			default:
				startLink = null;
			}

			this.setActiveApplication(activeApplication);
			if (applicationType === 'claim') {
				this.$router.push({ name: 'ClaimDetails', params: { claimId: this.id, size: this.applicationSize } });
			} else {
				this.$router.push({ name: startLink });
			}
		},
		...mapActions('settings', ['getDocManagementStatus']),
		...mapActions('application', ['setActiveApplication'])
	}

};
	</script>

<style scoped lang="scss">
	@import '../../assets/scss/components/applicationListItem.scss';
</style>
